import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from 'react'
import { rolesService } from 'api/configuration/roles'
import { AccessToCoursesEnum } from 'enum/accessToCourses'
import { AccessToLearningProgramsEnum } from 'enum/accessToLearningPrograms'
import { ICourse } from 'interfaces/learningPath'
import { ILearningProgram, IRole, IRoleDetail } from 'interfaces/roles'
import { initialRoleDetail } from './initialRoleDetail'

interface RolesAndPermissionsContextValue {
  roleOptions: IRole[]
  roleOptionsLoading: boolean
  roleOptionsLoaded: boolean
  fetchRoleOptions: () => Promise<IRole[]>
  roleDetail: IRoleDetail
  setRoleDetail: (data: IRoleDetail) => void
  resetRoleDetail: () => void
  setRoleName: (name: string) => void
  setRoleCode: (code: string) => void
  setRoleDescription: (description: string) => void
  setAccessToLearningPrograms: (value: AccessToLearningProgramsEnum) => void
  setAccessToCourses: (value: AccessToCoursesEnum) => void
  addLearningPrograms: (programs: ILearningProgram[]) => void
  addCourses: (courses: ICourse[]) => void
  removeLearningProgram: (id: number) => void
  removeCourse: (id: number) => void
  removeAllLearningPrograms: () => void
  removeAllCourses: () => void
  setAccess: (access: string, value: boolean) => void
}

const RolesAndPermissionsContext =
  createContext<RolesAndPermissionsContextValue | null>(null)

export const RolesAndPermissionsProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const [roleOptions, setRoleOptions] = useState<IRole[]>([])
  const [roleOptionsLoading, setRoleOptionsLoading] = useState(false)
  const [roleOptionsLoaded, setRoleOptionsLoaded] = useState(false)
  const [roleDetail, setRoleDetailState] =
    useState<IRoleDetail>(initialRoleDetail)

  const fetchRoleOptions = useCallback(async () => {
    setRoleOptionsLoading(true)
    try {
      const response = await rolesService.getRoleOptions()
      setRoleOptions(response.data.roles)
      setRoleOptionsLoaded(true)
      return response.data.roles
    } catch {
      setRoleOptions([])
      return []
    } finally {
      setRoleOptionsLoading(false)
    }
  }, [])

  const setRoleDetail = useCallback((data: IRoleDetail) => {
    setRoleDetailState(data)
  }, [])

  const resetRoleDetail = useCallback(() => {
    setRoleDetailState(initialRoleDetail)
  }, [])

  const setRoleName = useCallback((name: string) => {
    setRoleDetailState((state) => ({ ...state, name }))
  }, [])

  const setRoleCode = useCallback((code: string) => {
    setRoleDetailState((state) => ({ ...state, code }))
  }, [])

  const setRoleDescription = useCallback((description: string) => {
    setRoleDetailState((state) => ({ ...state, description }))
  }, [])

  const setAccessToLearningPrograms = useCallback(
    (value: AccessToLearningProgramsEnum) => {
      setRoleDetailState((state) => ({
        ...state,
        accessToLearningPrograms: value,
      }))
    },
    []
  )

  const setAccessToCourses = useCallback((value: AccessToCoursesEnum) => {
    setRoleDetailState((state) => ({ ...state, accessToCourses: value }))
  }, [])

  const addLearningPrograms = useCallback((programs: ILearningProgram[]) => {
    setRoleDetailState((state) => ({
      ...state,
      learningPrograms: [...state.learningPrograms, ...programs],
    }))
  }, [])

  const addCourses = useCallback((courses: ICourse[]) => {
    setRoleDetailState((state) => ({
      ...state,
      courses: [...state.courses, ...courses],
    }))
  }, [])

  const removeLearningProgram = useCallback((id: number) => {
    setRoleDetailState((state) => {
      const learningPrograms = state.learningPrograms.filter(
        (program) => program.id !== id
      )

      return {
        ...state,
        learningPrograms,
        accessToLearningPrograms:
          learningPrograms.length === 0
            ? AccessToLearningProgramsEnum.None
            : state.accessToLearningPrograms,
      }
    })
  }, [])

  const removeCourse = useCallback((id: number) => {
    setRoleDetailState((state) => {
      const courses = state.courses.filter((course) => course.id !== id)

      return {
        ...state,
        courses,
        accessToCourses:
          courses.length === 0
            ? AccessToCoursesEnum.None
            : state.accessToCourses,
      }
    })
  }, [])

  const removeAllLearningPrograms = useCallback(() => {
    setRoleDetailState((state) => ({ ...state, learningPrograms: [] }))
  }, [])

  const removeAllCourses = useCallback(() => {
    setRoleDetailState((state) => ({ ...state, courses: [] }))
  }, [])

  const setAccess = useCallback((access: string, value: boolean) => {
    setRoleDetailState((state) => ({
      ...state,
      accesses: { ...state.accesses, [access]: value },
    }))
  }, [])

  const value = useMemo(
    () => ({
      roleOptions,
      roleOptionsLoading,
      roleOptionsLoaded,
      fetchRoleOptions,
      roleDetail,
      setRoleDetail,
      resetRoleDetail,
      setRoleName,
      setRoleCode,
      setRoleDescription,
      setAccessToLearningPrograms,
      setAccessToCourses,
      addLearningPrograms,
      addCourses,
      removeLearningProgram,
      removeCourse,
      removeAllLearningPrograms,
      removeAllCourses,
      setAccess,
    }),
    [
      roleOptions,
      roleOptionsLoading,
      roleOptionsLoaded,
      fetchRoleOptions,
      roleDetail,
      setRoleDetail,
      resetRoleDetail,
      setRoleName,
      setRoleCode,
      setRoleDescription,
      setAccessToLearningPrograms,
      setAccessToCourses,
      addLearningPrograms,
      addCourses,
      removeLearningProgram,
      removeCourse,
      removeAllLearningPrograms,
      removeAllCourses,
      setAccess,
    ]
  )

  return (
    <RolesAndPermissionsContext.Provider value={value}>
      {children}
    </RolesAndPermissionsContext.Provider>
  )
}

export const useRolesAndPermissions = () => {
  const context = useContext(RolesAndPermissionsContext)

  if (!context) {
    throw new Error(
      'useRolesAndPermissions must be used within RolesAndPermissionsProvider'
    )
  }

  return context
}
